import { Link } from 'react-router';
import styled from 'styled-components';
import {
  Button as ReactstrapButton,
  type ButtonProps as ReactstrapButtonProps
} from 'reactstrap';
import { type FC } from 'react';
import Loading from './Loading';

interface ButtonProps extends ReactstrapButtonProps {
  loading?: boolean;
  size?: 'large' | 'small';
  color?: ReactstrapButtonProps['color'] | 'plus';
  textColor?: string;
  to?: string;
}

interface StyledButtonProps {
  color?: ButtonProps['color'];
  $textColor?: string;
  outline?: boolean;
  transparent?: boolean;
}

const Button: FC<ButtonProps> = ({
  loading = false,
  disabled = false,
  size = undefined,
  color = 'secondary',
  textColor = undefined,
  to,
  ...props
}) => {
  return (
    <StyledButton
      disabled={loading || disabled}
      size={size === 'large' ? 'lg' : size === 'small' ? 'sm' : undefined}
      tag={to ? Link : undefined}
      to={to}
      color={color}
      $textColor={textColor}
      {...props}>
      {loading && !disabled && (
        <LoadingContainer>
          <Loading size="small" color="currentColor" noContainer />
        </LoadingContainer>
      )}
      {props.children}
    </StyledButton>
  );
};

const StyledButton = styled(ReactstrapButton)<StyledButtonProps>`
  white-space: nowrap;
  border-radius: 0.5rem;

  &.disabled,
  &:disabled {
    border-color: var(--black);
    background-color: var(--gray-lighter);
    color: var(--black);
    opacity: 0.2;
  }

  &.btn-link {
    font-weight: 500;
  }

  transition: all 0.5s
    linear(
      0,
      1.114 8.5%,
      1.37 12.9%,
      1.315 16.2%,
      0.941 24%,
      0.869 27.8%,
      0.882 30.7%,
      1.012 38.3%,
      1.046 42.7%,
      0.984,
      1.006 72.2%,
      1
    )
    allow-discrete;

  &:hover {
    scale: 1.05;
  }

  ${({ color }) =>
    color === 'secondary'
      ? `
    color: var(--primary);
    &:hover {
      color: var(--primary);
    }
  `
      : color === 'plus'
        ? `
    background: linear-gradient(86.77deg, #4353ff -45.62%, #c343ff 116.68%);
    border: unset;
    color: var(--white);

  `
        : color !== 'primary' &&
          `
    background-color: ${color};
    &:hover {
      background-color: ${color};
    }`}

  ${({ outline }) =>
    outline &&
    `
    border-color: var(--gray-light);
    color: var(--primary);
    background-color: var(--white);
    &:hover {
      background-color: var(--gray-lightest);
      border-color: var(--gray-light);
      color: var(--primary);
    }
    &:focus, &:active {
      background-color: var(--gray-lightest) !important;
      border-color: var(--gray-light) !important;
      color: var(--primary) !important;
    }
    &.disabled,
    &:disabled {
      border-color: var(--black);
      background-color: var(--gray-lightest);
      color: var(--black);
      opacity: 0.2;
    }
    `}

  ${({ transparent }) =>
    transparent &&
    `
    border-color: var(--gray-light);
    color: var(--black);
    background-color: transparent;
    &:hover {
      background-color: transparent;
      border-color: var(--black);
      color: var(--black);
    }
    &:focus, &:active {
      background-color: transparent !important;
      border-color: var(--black) !important;
      color: var(--black) !important;
    }
    &.disabled,
    &:disabled {
      border-color: var(--gray-light);
      background-color: transparent;
      color: var(--gray-light);
      opacity: 0.2;
    }
    `}

  ${({ $textColor }) =>
    $textColor &&
    `color: ${$textColor};

    &:hover {
      color: ${$textColor};
    }`}
`;

const LoadingContainer = styled.span`
  margin-right: 0.5rem;
`;

export default Button;
